import React, { useState } from 'react';
import { Home, ChevronDown, X, Factory, MessageCircle } from 'lucide-react';

const MobileSidebar = ({
  isOpen,
  onClose,
  activeTab,
  setActiveTab,
  userdata,
  sidebarLogo,
  navigate,
  menuItems = [],
  groupedTabs = [],
  iconMap = {},
  badgeMap = null,
  canShowBackButton = false,
  customerDashboardPath = null
}) => {
  const [openGroup, setOpenGroup] = useState(null);

  if (!isOpen) return null;

  const otherMenuItems = (menuItems || []).filter((i) => i.name !== 'Inicio');

  const handleItemClick = (name) => {
    setActiveTab(name);
    onClose();
  };

  const handleFeedbackClick = () => {
    window.open('https://forms.gle/5hp68dMXkCKCi1si6', '_blank');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="absolute inset-y-0 left-0 w-[90%] max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500">
            <div className="flex items-center space-x-3">
              <img src={sidebarLogo} alt="Logo" className="h-10 w-auto" />
              <div>
                <h2 className="text-lg font-bold text-white">
                  {userdata?.Name || userdata?.projectName || 'Dashboard'}
                </h2>
                <p className="text-sm text-blue-100">Panel de Control</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 rounded-lg hover:bg-white/10 transition-colors active:scale-95"
            >
              <X size={24} className="text-white" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {(userdata?.Name || userdata?.projectName || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {userdata?.Name || userdata?.projectName || 'Usuario'}
                </p>
                <p className="text-sm text-gray-600">
                  {userdata?.identifier || 'ID no disponible'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {/* Inicio */}
            <button
              onClick={() => handleItemClick('Inicio')}
              className={`w-full flex items-center p-4 rounded-xl transition-all duration-200 text-left ${
                activeTab === 'Inicio'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'text-gray-700 hover:bg-gray-50 border border-transparent'
              }`}
            >
              <Home size={24} className={`mr-4 ${activeTab === 'Inicio' ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className="font-medium text-base">Inicio</span>
              {activeTab === 'Inicio' && <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full"></div>}
            </button>

            {/* Grouped tabs (same groups as desktop TopNavbar) */}
            {groupedTabs.map((group) => {
              if (group.single) {
                const name = group.items[0];
                return (
                  <button
                    key={group.label}
                    onClick={() => handleItemClick(name)}
                    className={`w-full flex items-center p-4 rounded-xl transition-all duration-200 text-left ${
                      activeTab === name
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className="font-medium text-base">{group.label}</span>
                    {activeTab === name && <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full"></div>}
                  </button>
                );
              }
              const isGroupActive = group.items.includes(activeTab);
              const isGroupOpen = openGroup === group.label || isGroupActive;
              return (
                <div key={group.label}>
                  <button
                    onClick={() => setOpenGroup((curr) => (curr === group.label ? null : group.label))}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 text-left ${
                      isGroupActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className="font-medium text-base">{group.label}</span>
                    <ChevronDown size={18} className={`transition-transform ${isGroupOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isGroupOpen && (
                    <div className="pl-4 mt-1 space-y-1">
                      {group.items.map((name) => (
                        <button
                          key={name}
                          onClick={() => handleItemClick(name)}
                          className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 text-left ${
                            activeTab === name
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                          }`}
                        >
                          <span className="mr-3 inline-flex items-center justify-center w-5 h-5">
                            {iconMap[name] || null}
                          </span>
                          <span className="font-medium text-sm">{name}</span>
                          {badgeMap && badgeMap[name] ? <span className="ml-2 rounded bg-gradient-to-r from-indigo-600 to-violet-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{badgeMap[name]}</span> : null}
                          {activeTab === name && <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full"></div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Other top-level items (outside groups, excluding Inicio) */}
            {otherMenuItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleItemClick(item.name)}
                className={`w-full flex items-center p-4 rounded-xl transition-all duration-200 text-left ${
                  activeTab === item.name
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                }`}
              >
                <span className="mr-4 inline-flex items-center justify-center w-6 h-6">
                  {item.icon}
                </span>
                <span className="font-medium text-base">{item.name}</span>
                {activeTab === item.name && <div className="ml-auto w-2 h-2 bg-blue-600 rounded-full"></div>}
              </button>
            ))}

            {/* Feedback */}
            <button
              onClick={handleFeedbackClick}
              className="w-full flex items-center p-4 rounded-xl transition-all duration-200 text-left text-gray-700 hover:bg-gray-50 border border-transparent"
            >
              <span className="font-medium text-base">Feedback</span>
            </button>
          </nav>

          {/* Footer Actions */}
          {canShowBackButton && customerDashboardPath && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  navigate(customerDashboardPath);
                  onClose();
                }}
                className="w-full flex items-center p-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors active:scale-95"
              >
                <Factory size={20} className="mr-3" />
                <span className="font-medium">Volver</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileSidebar;
